import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronUp, Printer, Download, Mail } from 'lucide-react';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  const effectiveDate = "January 1, 2026";

  // Table of contents sections
  const sections = [
    { id: 'agreement', title: 'Agreement to Terms' },
    { id: 'eligibility', title: 'Eligibility' },
    { id: 'account', title: 'Account Registration' },
    { id: 'service', title: 'Description of Service' },
    { id: 'user-content', title: 'User Content' },
    { id: 'acceptable-use', title: 'Acceptable Use' },
    { id: 'ai-features', title: 'AI Features' },
    { id: 'time-capsules', title: 'Time Capsules' },
    { id: 'inheritance', title: 'Inheritance Mode' },
    { id: 'payment', title: 'Subscription & Payment' },
    { id: 'ip', title: 'Intellectual Property' },
    { id: 'disclaimers', title: 'Disclaimers' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'disputes', title: 'Dispute Resolution' },
    { id: 'termination', title: 'Termination' },
    { id: 'general', title: 'General Provisions' },
    { id: 'contact', title: 'Contact Us' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);

      // Update active section based on scroll position
      const sectionElements = sections.map(s => ({
        id: s.id,
        element: document.getElementById(s.id)
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const el = sectionElements[i].element;
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(sectionElements[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#F5F5DC]">
      {/* Header */}
      <header className="bg-[#556B2F] text-white py-6 print:bg-white print:text-[#556B2F]">
        <div className="max-w-6xl mx-auto px-6">
          <Link to="/" className="text-2xl font-bold hover:opacity-90 transition-opacity">
            One Generation to Next
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation - Hidden on print */}
          <aside className="lg:w-72 flex-shrink-0 print:hidden">
            <div className="sticky top-8">
              <nav className="bg-[#F2F2F2] rounded-lg border border-[#CCCCCC] p-6">
                <h2 className="text-lg font-semibold text-[#556B2F] mb-4">Contents</h2>
                <ul className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`text-left w-full px-3 py-2 rounded-md text-sm transition-colors ${
                          activeSection === section.id
                            ? 'bg-[#556B2F] text-white'
                            : 'text-[#333333] hover:bg-[#6B8E23] hover:text-white'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Actions */}
                <div className="mt-6 pt-6 border-t border-[#CCCCCC] space-y-3">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Printer size={16} />
                    Print this page
                  </button>
                  <a
                    href="/documents/1G2N_Terms_of_Service.pdf"
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Download size={16} />
                    Download PDF
                  </a>
                  <a
                    href="mailto:legal@1g2n.com"
                    className="flex items-center gap-2 text-sm text-[#556B2F] hover:text-[#6B8E23] transition-colors"
                  >
                    <Mail size={16} />
                    Contact us
                  </a>
                </div>
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <article className="bg-white rounded-lg border border-[#CCCCCC] p-8 lg:p-12 print:border-none print:p-0">
              {/* Title */}
              <header className="mb-10 pb-8 border-b border-[#CCCCCC]">
                <h1 className="text-4xl lg:text-5xl font-bold text-[#556B2F] mb-4">
                  Terms of Service
                </h1>
                <p className="text-lg text-[#666666]">
                  Effective Date: {effectiveDate}
                </p>
              </header>

              {/* Content */}
              <div className="prose prose-lg max-w-none text-[#333333]">

                {/* Agreement to Terms */}
                <section id="agreement" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Agreement to Terms</h2>
                  <p className="mb-4 leading-relaxed">
                    Welcome to One Generation to Next ("1G2N," "we," "us," or "our"). These Terms of Service ("Terms") govern your access to and use of our family legacy platform, including our website, mobile applications, and related services (collectively, the "Service").
                  </p>
                  <p className="mb-4 leading-relaxed">
                    By creating an account or using our Service, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Service.
                  </p>
                  <div className="bg-[#F5F5DC] border-l-4 border-[#E65100] p-4 my-6">
                    <p className="text-[#333333] font-medium">
                      <strong>PLEASE READ THESE TERMS CAREFULLY.</strong> They contain important information about your rights and obligations, including limitations of liability and a binding arbitration clause.
                    </p>
                  </div>
                </section>

                {/* Eligibility */}
                <section id="eligibility" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Eligibility</h2>
                  <p className="mb-4 leading-relaxed">To use our Service, you must:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Be at least 18 years of age</li>
                    <li>Have the legal capacity to enter into a binding agreement</li>
                    <li>Not be prohibited from using the Service under applicable law</li>
                  </ul>
                  <p className="leading-relaxed">
                    If you are accessing the Service on behalf of a family member or organization, you represent that you have the authority to bind them to these Terms.
                  </p>
                </section>

                {/* Account Registration */}
                <section id="account" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Account Registration and Security</h2>
                  <p className="mb-4 leading-relaxed">
                    To access most features of our Service, you must create an account. When you register, you agree to:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Provide accurate, current, and complete information</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Keep your password secure and confidential</li>
                    <li>Accept responsibility for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                  </ul>
                  <p className="leading-relaxed">
                    We reserve the right to suspend or terminate accounts that violate these Terms or that we reasonably believe may be used for fraudulent or illegal purposes.
                  </p>
                </section>

                {/* Description of Service */}
                <section id="service" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Description of Service</h2>
                  <p className="mb-6 leading-relaxed">
                    One Generation to Next is a family legacy platform that enables families to preserve and share wisdom, stories, and memories across generations. Our Service includes:
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Core Features</h3>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Story creation and preservation tools</li>
                    <li>Family tree building and relationship mapping</li>
                    <li>Photo and video storage and sharing</li>
                    <li>Recipe collection and preservation</li>
                    <li>Life Lesson Playlists and Wisdom Index</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Advanced Features</h3>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Digital Time Capsules with scheduled delivery</li>
                    <li>Voiceprint Memories for voice preservation</li>
                    <li>AI-powered features (with explicit consent)</li>
                    <li>Inheritance Mode for legacy transfer</li>
                    <li>Multi-format export options</li>
                  </ul>
                </section>

                {/* User Content */}
                <section id="user-content" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">User Content</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Your Content and Ownership</h3>
                  <p className="mb-6 leading-relaxed">
                    You retain all ownership rights to the content you upload, create, or share through our Service ("User Content"). This includes your stories, photographs, videos, voice recordings, recipes, and other materials.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">License to Us</h3>
                  <p className="mb-4 leading-relaxed">
                    By uploading User Content, you grant us a limited, non-exclusive, worldwide license to store, display, reproduce, and transmit your content solely for the purpose of providing the Service to you and your designated family members. This license includes the right to:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Store your content on our servers and backup systems</li>
                    <li>Display your content to users you have authorized</li>
                    <li>Process your content to provide features you have enabled</li>
                    <li>Create derivative works for AI features (with your explicit consent)</li>
                  </ul>
                  <p className="mb-6 leading-relaxed">
                    This license terminates when you delete your content or close your account, except for content that has been shared with family members or included in time capsules that have not yet been delivered.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Content Responsibilities</h3>
                  <p className="mb-4 leading-relaxed">
                    You are solely responsible for your User Content. You represent and warrant that:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>You own or have the necessary rights to share the content</li>
                    <li>Your content does not infringe any third party rights</li>
                    <li>Your content complies with these Terms and applicable law</li>
                    <li>You have consent from individuals identifiable in your content</li>
                  </ul>
                </section>

                {/* Acceptable Use */}
                <section id="acceptable-use" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Acceptable Use Policy</h2>
                  <p className="mb-4 leading-relaxed">You agree not to use the Service to:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Upload content that is illegal, harmful, threatening, abusive, or defamatory</li>
                    <li>Impersonate any person or entity</li>
                    <li>Upload viruses, malware, or other harmful code</li>
                    <li>Attempt to gain unauthorized access to our systems</li>
                    <li>Interfere with other users' enjoyment of the Service</li>
                    <li>Use the Service for commercial purposes without authorization</li>
                    <li>Violate any applicable laws or regulations</li>
                    <li>Collect or harvest user data without consent</li>
                  </ul>
                  <p className="leading-relaxed">
                    We reserve the right to remove content and suspend or terminate accounts that violate this policy.
                  </p>
                </section>

                {/* AI Features */}
                <section id="ai-features" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">AI Features and Voice Technology</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Voiceprint Memories</h3>
                  <p className="mb-4 leading-relaxed">
                    Our Voiceprint Memories feature allows you to preserve and recreate your voice using AI technology. By enabling this feature, you:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Consent to the recording and processing of your voice samples</li>
                    <li>Understand that AI models will be created from your voice data</li>
                    <li>Acknowledge that voice synthesis technology has limitations</li>
                    <li>Accept that recreated voices may not perfectly match original voices</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">AI Conversation Simulation</h3>
                  <p className="mb-4 leading-relaxed">
                    Our AI Conversation Simulation feature allows family members to have simulated conversations based on a user's recorded content and voice. This feature requires explicit opt-in consent and is subject to the following conditions:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Simulations are clearly labeled as AI-generated</li>
                    <li>Family consent is required before enabling this feature</li>
                    <li>Users can revoke consent and request deletion of AI models</li>
                    <li>We make no guarantees about accuracy or appropriateness of AI responses</li>
                  </ul>
                  <div className="bg-[#F5F5DC] border-l-4 border-[#556B2F] p-4 my-6">
                    <p className="text-[#333333]">
                      We approach AI features with respect for dignity and authenticity. These features are designed to preserve wisdom and connection, not to deceive or replace human relationships.
                    </p>
                  </div>
                </section>

                {/* Time Capsules */}
                <section id="time-capsules" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Time Capsules and Scheduled Delivery</h2>
                  <p className="mb-4 leading-relaxed">
                    Digital Time Capsules allow you to create content for future delivery. By using this feature, you understand and agree:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Content will be stored securely until the scheduled delivery date</li>
                    <li>Delivery depends on accurate recipient contact information</li>
                    <li>We will make reasonable efforts to deliver content as scheduled</li>
                    <li>Delivery may be delayed or prevented by technical issues beyond our control</li>
                    <li>You may modify or cancel scheduled deliveries before they occur</li>
                  </ul>
                  <div className="bg-[#F5F5DC] border-l-4 border-[#E65100] p-4 my-6">
                    <p className="text-[#333333] font-medium">
                      <strong>Important:</strong> Once content has been delivered, it cannot be recalled. Please carefully consider the content and timing of your time capsules.
                    </p>
                  </div>
                </section>

                {/* Inheritance Mode */}
                <section id="inheritance" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Inheritance and Legacy Transfer</h2>
                  <p className="mb-4 leading-relaxed">
                    Our Inheritance Mode allows you to designate family members who should receive access to your content in specified circumstances. By using this feature:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>You are responsible for keeping heir designations up to date</li>
                    <li>We may require documentation to verify inheritance claims</li>
                    <li>Transfer timing depends on the triggers you have specified</li>
                    <li>We are not responsible for family disputes regarding inheritance</li>
                  </ul>
                  <p className="leading-relaxed">
                    Inheritance features are not a substitute for legal estate planning. We recommend consulting with an attorney for comprehensive legacy planning.
                  </p>
                </section>

                {/* Payment Terms */}
                <section id="payment" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Subscription and Payment</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Subscription Plans</h3>
                  <p className="mb-6 leading-relaxed">
                    One Generation to Next offers subscription-based access to our Service. Subscription details, pricing, and features are available on our website and may change from time to time.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Billing</h3>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Subscriptions are billed in advance on a monthly or annual basis</li>
                    <li>All fees are non-refundable except as required by law</li>
                    <li>You authorize us to charge your payment method automatically</li>
                    <li>You are responsible for providing accurate payment information</li>
                  </ul>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Cancellation</h3>
                  <p className="mb-4 leading-relaxed">
                    You may cancel your subscription at any time through your account settings. Upon cancellation:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>You retain access until the end of your current billing period</li>
                    <li>Your content remains accessible in read-only mode for 90 days</li>
                    <li>You may export your content before your account becomes inactive</li>
                    <li>After 90 days, your content may be deleted</li>
                  </ul>
                </section>

                {/* Intellectual Property */}
                <section id="ip" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Intellectual Property</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Our Rights</h3>
                  <p className="mb-6 leading-relaxed">
                    The Service, including its original content, features, and functionality, is owned by One Generation to Next and is protected by copyright, trademark, and other intellectual property laws. Our trademarks, logos, and service marks may not be used without our prior written consent.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Feedback</h3>
                  <p className="leading-relaxed">
                    If you provide suggestions, ideas, or other feedback about the Service, you grant us the right to use such feedback without restriction or compensation to you.
                  </p>
                </section>

                {/* Disclaimers */}
                <section id="disclaimers" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Disclaimers</h2>
                  <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-lg p-6 mb-6">
                    <p className="font-medium text-[#333333] uppercase">
                      THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                    </p>
                  </div>
                  <p className="mb-4 leading-relaxed">We do not warrant that:</p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>The Service will be uninterrupted, secure, or error-free</li>
                    <li>Results from the Service will be accurate or reliable</li>
                    <li>The quality of any content will meet your expectations</li>
                    <li>Any errors in the Service will be corrected</li>
                  </ul>
                  <p className="leading-relaxed">
                    AI-generated content, including voice synthesis and conversation simulations, may contain inaccuracies or inappropriate responses. Users should not rely on AI features for critical decisions.
                  </p>
                </section>

                {/* Limitation of Liability */}
                <section id="liability" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Limitation of Liability</h2>
                  <div className="bg-[#F2F2F2] border border-[#CCCCCC] rounded-lg p-6 mb-6">
                    <p className="font-medium text-[#333333] uppercase text-sm">
                      TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ONE GENERATION TO NEXT, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION:
                    </p>
                  </div>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Loss of profits, data, use, or goodwill</li>
                    <li>Cost of substitute services</li>
                    <li>Any other intangible losses</li>
                  </ul>
                  <p className="mb-4 leading-relaxed">
                    Our total liability for any claims arising out of or relating to these Terms or the Service shall not exceed the amount you paid us in the twelve months preceding the claim.
                  </p>
                  <p className="leading-relaxed text-[#666666] text-sm">
                    Some jurisdictions do not allow the exclusion or limitation of certain damages, so some of the above limitations may not apply to you.
                  </p>
                </section>

                {/* Dispute Resolution */}
                <section id="disputes" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Dispute Resolution</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Informal Resolution</h3>
                  <p className="mb-6 leading-relaxed">
                    Before filing a claim, you agree to try to resolve the dispute informally by contacting us at legal@1g2n.com. We will try to resolve the dispute informally within 30 days.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Arbitration</h3>
                  <p className="mb-4 leading-relaxed">
                    If we cannot resolve the dispute informally, you and One Generation to Next agree to resolve any claims through binding arbitration, rather than in court, except that either party may bring claims in small claims court if eligible.
                  </p>
                  <p className="mb-6 leading-relaxed">
                    The arbitration shall be administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration will be conducted in the English language in [Your State/Province].
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Class Action Waiver</h3>
                  <div className="bg-[#F5F5DC] border-l-4 border-[#E65100] p-4 my-6">
                    <p className="text-[#333333] font-medium uppercase text-sm">
                      YOU AND ONE GENERATION TO NEXT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION.
                    </p>
                  </div>
                </section>

                {/* Termination */}
                <section id="termination" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Termination</h2>
                  <p className="mb-4 leading-relaxed">
                    We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including if you breach these Terms. Upon termination:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li>Your right to use the Service will immediately cease</li>
                    <li>You may request a copy of your content for 30 days after termination</li>
                    <li>We may delete your content after the 30-day period</li>
                    <li>Provisions that should survive termination will remain in effect</li>
                  </ul>
                  <p className="leading-relaxed">
                    You may terminate your account at any time by contacting us or using the account deletion feature in settings.
                  </p>
                </section>

                {/* General Provisions */}
                <section id="general" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">General Provisions</h2>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4">Entire Agreement</h3>
                  <p className="mb-6 leading-relaxed">
                    These Terms, together with our Privacy Policy, constitute the entire agreement between you and One Generation to Next regarding the Service and supersede any prior agreements.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Severability</h3>
                  <p className="mb-6 leading-relaxed">
                    If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Waiver</h3>
                  <p className="mb-6 leading-relaxed">
                    Our failure to enforce any right or provision of these Terms shall not be considered a waiver of those rights.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Assignment</h3>
                  <p className="mb-6 leading-relaxed">
                    You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.
                  </p>

                  <h3 className="text-xl lg:text-2xl font-semibold text-[#6B8E23] mb-4 mt-8">Governing Law</h3>
                  <p className="leading-relaxed">
                    These Terms shall be governed by the laws of [Your State/Country], without regard to its conflict of law provisions.
                  </p>
                </section>

                {/* Changes to Terms */}
                <section id="changes" className="mb-12">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Changes to Terms</h2>
                  <p className="mb-4 leading-relaxed">
                    We may modify these Terms at any time. If we make material changes, we will notify you by email or through the Service at least 30 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the modified Terms.
                  </p>
                  <p className="leading-relaxed">
                    If you do not agree to the modified Terms, you must stop using the Service and may request deletion of your account.
                  </p>
                </section>

                {/* Contact Us */}
                <section id="contact" className="mb-8">
                  <h2 className="text-2xl lg:text-3xl font-bold text-[#556B2F] mb-6">Contact Us</h2>
                  <p className="mb-6 leading-relaxed">
                    If you have any questions about these Terms, please contact us:
                  </p>
                  <div className="bg-[#F2F2F2] rounded-lg p-6 border border-[#CCCCCC]">
                    <p className="font-semibold text-[#556B2F] text-lg mb-2">One Generation to Next</p>
                    <p className="mb-1">
                      <strong>Email:</strong>{' '}
                      <a href="mailto:legal@1g2n.com" className="text-[#6B8E23] hover:underline">
                        legal@1g2n.com
                      </a>
                    </p>
                    <p>
                      <strong>Address:</strong> [Your Business Address]
                    </p>
                  </div>
                </section>

              </div>
            </article>

            {/* Related Links */}
            <div className="mt-8 p-6 bg-[#F2F2F2] rounded-lg border border-[#CCCCCC] print:hidden">
              <h3 className="font-semibold text-[#556B2F] mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/privacy"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cookies"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Cookie Policy
                </Link>
                <Link
                  to="/acceptable-use"
                  className="text-[#6B8E23] hover:text-[#556B2F] hover:underline"
                >
                  Acceptable Use Policy
                </Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-[#556B2F] text-white p-3 rounded-full shadow-lg hover:bg-[#6B8E23] transition-colors print:hidden"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}

      {/* Footer */}
      <footer className="bg-[#556B2F] text-white py-8 mt-12 print:hidden">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-sm opacity-90">
            &copy; {new Date().getFullYear()} One Generation to Next. All rights reserved.
          </p>
          <div className="mt-4 flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:underline opacity-90 hover:opacity-100">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:underline opacity-90 hover:opacity-100">
              Terms of Service
            </Link>
            <Link to="/contact" className="hover:underline opacity-90 hover:opacity-100">
              Contact Us
            </Link>
          </div>
        </div>
      </footer>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            font-size: 12pt;
            line-height: 1.5;
          }
          .prose {
            max-width: none;
          }
          h1 {
            font-size: 24pt;
          }
          h2 {
            font-size: 18pt;
            page-break-after: avoid;
          }
          h3 {
            font-size: 14pt;
            page-break-after: avoid;
          }
          ul, p {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;
